<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Plans Description') }}
    </div>
    <div class="wrapper">
      <div v-if="loading" class="loading-overlay">
        <loading-circle width="32" height="32" layout="inline" />
      </div>
      <template v-else>
        <div class="list">
          <ChangePlanItem
            v-for="item in store.billingPlans"
            :key="item.id"
            :plan="item"
            :show-select-plan-button="true"
            :selected-plan-id="selectedPlanId"
            :handle-plan-select="() => selectPlan(item)"
          />
        </div>
        <div class="compare">
          <div class="compare__header">
            <div class="compare__text-column">{{ t('Compare Features') }}</div>
            <div
              v-for="plan in store.billingPlans"
              :key="plan.id"
              class="compare__price-column"
            >
              <span class="plan-name" :class="[`item_${plan.id}`]">{{ plan.name }}</span>
            </div>
          </div>
          <div class="compare__body">
            <div
              v-for="block in blockRowsFiltered"
              :key="block.name"
              class="compare__row-block"
            >
              <div class="compare__row-block-name">
                <div class="compare__text-column">{{ block.name }}</div>
                <div
                  v-for="plan in store.billingPlans"
                  :key="`${block.name}-${plan.id}`"
                  class="compare__price-column"
                  :class="{
                    'compare__price-column--selected': plan.id === selectedPlanId,
                  }"
                />
              </div>
              <div
                v-for="key in block.keys"
                :key="`${block.name}-${key}`"
                class="compare__row"
              >
                <div class="compare__text-column">{{ planLimitsKeyTextShort[key] }}</div>
                <div
                  v-for="plan in store.billingPlans"
                  :key="key + plan.id"
                  class="compare__price-column"
                  :class="{
                    'compare__price-column--selected': plan.id === selectedPlanId,
                  }"
                >
                  <ui-icon
                    v-if="plan.limits[key] === true"
                    :color="colors.green"
                    size="20"
                    icon="fas fa-check"
                  />
                  <ui-icon
                    v-else-if="plan.limits[key] === false"
                    :color="colors.red"
                    size="20"
                    icon="fas fa-times"
                  />
                  <span v-else class="compare__price-column--number">{{
                    plan.limits[key]
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="compare__end-row">
            <div class="compare__text-column" />
            <div
              v-for="plan in store.billingPlans"
              :key="`end-${plan.id}`"
              class="compare__price-column"
            >
              <span v-if="plan.id === selectedPlanId" class="current-plan-text">{{
                t('Current Plan')
              }}</span>
              <ui-button
                v-else
                :text="t('Select Plan')"
                icon="fas fa-check"
                button-style="accent"
                @click="selectPlan(plan)"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n, useColors, usePlanLimits } from '@/composables';
import { $emitter } from '@/services/emitter';
import { getBillingPlansApiRequest } from '@/services/api/billing-api';
import { useStore } from '@/store';
import type { BillingPlan } from '@/services/models';
import ChangePlanItem from '@/views/settings/subscription/forms/change-plan/change-plan-item.vue';
import LoadingCircle from '@/components/loading-circle.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const colors = useColors();

const { planLimitsKeysGrouping, planLimitsKeyTextShort } = usePlanLimits(
  undefined,
  false,
);

const loading = ref(true);
const selectedPlanId = computed(() => store.billing.planId);

const blockRowsFiltered = computed(() => {
  const existedKeysInFirstPlan = store.billingPlans[0].limits;
  return planLimitsKeysGrouping.value
    .map(block => {
      const filteredKeys = block.keys.filter(key => key in existedKeysInFirstPlan);
      return {
        ...block,
        keys: filteredKeys,
      };
    })
    .filter(item => !item.name.includes('Data retention'));
});

const selectPlan = (plan: BillingPlan) => {
  $emitter.emit('open-dialog', {
    name: 'ChangePlanDialog',
    data: {
      preSelectedPlan: plan,
    },
  });
};

onMounted(() => {
  getBillingPlansApiRequest()
    .then(plans => {
      store.setBillingPlans(plans);
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style lang="scss" scoped>
.wrapper {
  width: 100%;
}

.compare {
  color: var(--color-bluedeep);
  display: inline-block;
  max-width: 912px;
  min-width: 100%;
  overflow-x: auto;
  margin-bottom: 24px;
  padding-bottom: 24px;
}

.compare__header,
.compare__end-row,
.compare__body {
  width: 100%;

  .compare__text-column {
    width: 290px;
    min-width: 250px;
    padding: 5px 0;
  }

  .compare__price-column {
    width: calc((100% - 290px) / 4);
    min-width: 100px;
    align-items: center;
    justify-content: center;
    display: flex;
  }
}

.compare__end-row {
  display: flex;
  padding-top: 33px;
}

.compare__header {
  display: flex;
  background: var(--color-gray3);
  position: sticky;
  z-index: 100;
  top: 0;
  border-top: 2px solid var(--color-gray10);
  border-bottom: 2px solid var(--color-gray10);
  align-items: center;

  .compare__text-column {
    font-size: 20px;
    font-weight: bold;
    padding: 5px 0;
  }

  .compare__price-column {
    padding: 13px 5px;

    span {
      padding: 4px 8px;
      border-radius: 8px;
      font-size: 14px;
      color: var(--color-white);
      line-height: 1.14;
      font-weight: bold;
    }

    .item_free,
    .item_basic {
      background-color: var(--color-green);
    }

    [class*='item_professional'] {
      background-color: var(--color-grass);
    }

    [class*='item_business'] {
      background-color: var(--color-blue);
    }

    [class*='item_enterprise'] {
      background-color: var(--color-bluedark);
    }

    [class*='item_team'] {
      background-color: var(--color-tangerine);
    }
  }
}

.compare__body {
  .compare__text-column {
    font-size: 14px;
    font-weight: bold;
    text-transform: capitalize;
  }

  .compare__price-column {
    border-left: 1px solid var(--color-gray10);
  }

  .compare__price-column--selected {
    position: relative;

    &:after,
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      height: 100%;
      width: 2px;
      background: var(--color-green);
    }

    &:before {
      left: -1px;
    }

    &:after {
      right: -1px;
    }
  }
}

.compare__row {
  display: flex;
  position: relative;
  z-index: 1;

  &:hover {
    &:after {
      position: absolute;
      content: '';
      display: block;
      background: var(--color-green10);
      border-radius: 8px;
      padding: 16px;
      left: -8px;
      right: -8px;
      z-index: -1;
    }
  }
}

.compare__row-block-name {
  display: flex;

  .compare__text-column {
    font-size: 20px;
    font-weight: bold;
    padding: 13px 0 6px;
  }
}

.compare__price-column--number {
  font-weight: bold;
}

.list {
  display: flex;
  padding: 0;
  overflow-x: auto;

  .item {
    margin: 8px 16px 32px 0;
    width: 216px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.current-plan-text {
  color: var(--color-green);
  font-size: 12px;
}

.loading-circle {
  width: 100%;
  display: flex;
  height: 200px;
  align-items: center;
  justify-content: center;
}
</style>
